/**
 * Attendance — server state.
 *
 * Every mutation invalidates the volunteer keys too: hours appear on the
 * volunteer's profile as well as on the activity and the attendance queue, and a
 * decision that left one of those stale would have staff reading two different
 * answers to "has this been approved?" on two screens.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  correctAttendance,
  createAttendance,
  fetchActivityAttendance,
  fetchAttendance,
  fetchAttendanceHistory,
  listMyAttendance,
  overrideAttendance,
} from '../services/api/attendance';
import type { AttendanceDetail } from '@parinaam-vms/shared';
import type {
  AttendanceListQuery,
  CorrectAttendanceBody,
  CreateAttendanceBody,
} from '../services/api/attendance';

import { attendanceKeys, volunteerKeys } from './queryKeys';
import { sessionKeys } from './useSessions';

export { attendanceKeys };

export function useAttendance(params: AttendanceListQuery) {
  return useQuery({
    queryKey: attendanceKeys.list(params),
    queryFn: () => fetchAttendance(params),
    // A queue of decisions several people work at once.
    staleTime: 0,
  });
}

export function useActivityAttendance(activityId: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.forActivity(activityId ?? ''),
    queryFn: () => fetchActivityAttendance(activityId as string),
    enabled: Boolean(activityId),
    staleTime: 0,
  });
}

function useInvalidateAttendance() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
    void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
    // The volunteer-first form suggests sessions ordered by when this volunteer
    // was last recorded at each. Recording changes that order, so a stale list
    // would keep putting last month's session above the one just used.
    void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
  };
}

export function useCreateAttendance() {
  const invalidate = useInvalidateAttendance();
  const toast = useToast();

  return useMutation({
    mutationFn: (body: CreateAttendanceBody) => createAttendance(body),
    onSuccess: () => {
      invalidate();
      toast.success('Attendance recorded.');
    },
    onError: (error) => toast.error(error, 'Could not record the attendance.'),
  });
}

/*
 * `useApproveAttendance` and `useRejectAttendance` were here until
 * 8 Oct 2026. Recording hours approves them, so the endpoints they called no
 * longer exist and nothing in the portal offers either decision.
 */

export function useCorrectAttendance() {
  const invalidate = useInvalidateAttendance();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CorrectAttendanceBody }) =>
      correctAttendance(id, body),
    onSuccess: () => {
      invalidate();
      toast.success('Hours corrected.');
    },
    onError: (error) => toast.error(error, 'Could not correct the hours.'),
  });
}

export function useOverrideAttendance() {
  const invalidate = useInvalidateAttendance();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, minutes, reason }: { id: string; minutes: number; reason: string }) =>
      overrideAttendance(id, minutes, reason),
    onSuccess: () => {
      invalidate();
      toast.success('Hours overridden.');
    },
    onError: (error) => toast.error(error, 'Could not override the hours.'),
  });
}

/**
 * The audit trail for one attendance record (M2, §11.53).
 *
 * `staleTime: 0` for the same reason as the volunteer history: the panel sits
 * beside the controls that write to it, and a cached trail would omit the change
 * the user just made.
 */
export function useAttendanceHistory(id: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.history(id ?? ''),
    queryFn: () => fetchAttendanceHistory(id as string),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

/** The signed-in volunteer's own hours. Read-only — see MyHoursPage. */
export function useMyAttendance() {
  return useQuery<AttendanceDetail[]>({
    queryKey: ['attendance', 'mine'],
    queryFn: listMyAttendance,
  });
}
